import { LogOut, Shield, User } from "lucide-react";
import { Link, useNavigate } from "react-router";
import type { MeResponse } from "@guestbook/shared";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/shared/components/ui/DropdownMenu";
import { useLogout, useMe } from "@/shared/lib/auth";

interface AccountMenuProps {
  onNavigate?: () => void;
}

export function getInitials(user: MeResponse | null | undefined): string {
  if (!user) return "?";
  const name = user.name?.trim();
  if (name) {
    const tokens = name.split(/\s+/).filter(Boolean);
    const firstChar = tokens[0]?.[0];
    const lastChar = tokens[tokens.length - 1]?.[0];
    if (tokens.length >= 2 && firstChar && lastChar) {
      return (firstChar + lastChar).toUpperCase();
    }
    if (firstChar) {
      return firstChar.toUpperCase();
    }
  }
  const emailChar = user.email.split("@")[0]?.[0];
  if (emailChar) {
    return emailChar.toUpperCase();
  }
  return "?";
}

/**
 * Top-right account dropdown. Trigger is a circular glass avatar that
 * shows the signed-in user's initials (two from the name when available,
 * otherwise the first letter of the email local-part); the menu opens
 * downward and right-aligned. Profile is a placeholder route; logout
 * calls /auth/logout then routes to /auth/login regardless of outcome.
 */
export function AccountMenu({ onNavigate }: AccountMenuProps) {
  const me = useMe();
  const logout = useLogout();
  const navigate = useNavigate();

  function handleLogout() {
    logout.mutate(undefined, {
      onSettled: () => navigate("/auth/login"),
    });
  }

  const initials = getInitials(me.data);

  return (
    <DropdownMenu>
      <DropdownMenuTrigger
        className="inline-flex h-9 w-9 items-center justify-center rounded-full glass-surface-subtle type-label-base text-text transition-colors duration-150 hover:glass-surface focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand"
        aria-label="Account menu"
      >
        <span aria-hidden>{initials}</span>
      </DropdownMenuTrigger>
      <DropdownMenuContent side="bottom" align="end" className="w-[15rem]">
        <DropdownMenuItem asChild>
          <Link to="/app/profile" onClick={onNavigate}>
            <User aria-hidden className="h-4 w-4 text-text-muted" />
            <span>Profile</span>
          </Link>
        </DropdownMenuItem>
        {me.data?.isAdmin ? (
          <DropdownMenuItem asChild>
            <Link to="/app/admin" onClick={onNavigate}>
              <Shield aria-hidden className="h-4 w-4 text-text-muted" />
              <span>Admin</span>
            </Link>
          </DropdownMenuItem>
        ) : null}
        <DropdownMenuSeparator />
        <DropdownMenuItem onSelect={handleLogout} disabled={logout.isPending}>
          <LogOut aria-hidden className="h-4 w-4 text-text-muted" />
          <span>{logout.isPending ? "Signing out…" : "Sign out"}</span>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
